import React, { useEffect, useState } from 'react'
import domPurify from 'dompurify'
import moment, { Moment } from 'moment'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import InputLabel from '@mui/material/InputLabel'
import { useAppState } from 'Context/AppContext/AppContext'
import { Modal } from '@material-ui/core'
import InfoIcon from '@mui/icons-material/Info'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import { Button, colors } from '@resiliatech/birdsong'
import { Resource } from 'Generated/academy-graphql-types'
import { GoalType } from 'Generated/graphql-types'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import TooltipSong from 'Components/BirdSong/TooltipSong/TooltipSong'
import { ChecklistItemWithResourceInfo } from 'TypeDefs/Goal'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import {
  FlexRow,
  TaskModalButtonContainer,
  TaskModalContainer,
  TaskModalHeader,
  TaskModalInputsContainer,
  TaskModalSubHeader,
} from './TaskModal.styled'
import { AcademyResourceSearch } from '../../../../Impact/GoalPage/Tasks/ResourceTasks/AcademyResourceSearch'
import { MINIMUM_PROTOCOL } from '../TaskItem'
import { DatePicker } from '../../../../../SharedComponents/DatePicker/DatePicker'

type TaskModalProps = {
  goalId: number
  isOpen: boolean
  onClose: () => void
  onSubmitTask: (
    task: Omit<ChecklistItemWithResourceInfo, 'createdAt' | 'updatedAt'>,
    toastMessage: string
  ) => Promise<undefined | number>
  task?: ChecklistItemWithResourceInfo
  eventProps: {
    goalType: GoalType
    templateLibraryId: number | null
    initiativeId?: number
    goalId: number
  }
}

export const TaskModal = ({
  goalId,
  isOpen,
  onClose,
  onSubmitTask,
  task,
  eventProps,
}: TaskModalProps) => {
  const { teamOptions, teamMap = {} } = useNonprofitOrgTeam()
  const [taskTitle, setTaskTitle] = useState('')
  const [taskOwnerId, setTaskOwnerId] = useState(0)
  const [dueDate, setDueDate] = useState<Moment | null>(null)
  const [academyResource, setAcademyResource] = useState<
    Resource | null | undefined
  >(null)
  const [taskLinkText, setTaskLinkText] = useState('')
  const [taskLinkUrl, setTaskLinkUrl] = useState('')
  const [isAssignmentOpen, setIsAssignmentOpen] = useState(false)
  const {
    userConfig: { id: userId },
  } = useAppState()

  // Trigger MUI component re-renders when the active task changes/updates
  useEffect(() => {
    if (task) {
      setTaskTitle(task.title)
      setTaskOwnerId(task.owner?.id ?? 0)
      setDueDate(task.dueDate ? moment.utc(task.dueDate) : null)
      setAcademyResource(task.resource)
      setTaskLinkText(task.linkText ?? '')
      setTaskLinkUrl(task.linkUrl ?? '')
    }
  }, [task])

  // Reset payload for task-to-upsert on modal close
  const handleClose = () => {
    setTaskTitle('')
    setTaskOwnerId(0)
    setDueDate(null)
    setAcademyResource(null)
    setTaskLinkText('')
    setTaskLinkUrl('')
    onClose()
    analytics.trackActionCreator(initiatives.Goal_Task_Cancel_Clicked)({
      ...eventProps,
      resourceId: task?.resource,
      taskDescription: task?.title,
      taskId: task?.id,
      taskStatus: task?.isCompleted ? 'complete' : 'pending',
      taskUpdatedDate: new Date().toISOString().substring(0, 10),
      dueDate: task?.dueDate,
    })
  }

  const validateUrl = (linkUrl: string) => {
    let newLinkUrl = linkUrl
    // Add minimum protocol check for best UX since we've decided not to do true validation
    if (linkUrl && !linkUrl.includes(MINIMUM_PROTOCOL))
      newLinkUrl = `https://${linkUrl}`

    return newLinkUrl
  }

  let taskAction = ''
  let toastMessage = ''
  if (!task) taskAction = 'New Task'
  else {
    taskAction = task.id ? 'Edit Task' : 'Duplicate Task'
    toastMessage = task.id
      ? 'Your have made edits to your task'
      : 'Your task has been duplicated!'
  }

  const shouldHighlightAssignOwner =
    !taskOwnerId && ['Edit Task', 'Duplicate Task'].includes(taskAction)

  const handleSubmitTask = async () => {
    const owner = { id: taskOwnerId } as ChecklistItemWithResourceInfo['owner']

    const taskToUpsert = {
      createdAt: task?.createdAt,
      dueDate: dueDate ? dueDate.format('YYYY-MM-DD') : null,
      goalId,
      id: task?.id,
      isCompleted: task?.isCompleted ?? false,
      linkText: taskLinkText || null,
      linkUrl: validateUrl(taskLinkUrl) || null,
      owner: owner || null,
      resource: academyResource ?? null,
      title: taskTitle || '',
    }

    const taskId = await onSubmitTask(taskToUpsert, toastMessage)
    if (taskId) {
      const eventData = {
        ...eventProps,
        resourceId: taskToUpsert.resource?.id,
        taskDescription: taskToUpsert.title,
        taskStatus: taskToUpsert.isCompleted ? 'complete' : 'pending',
        taskUpdatedDate: new Date().toISOString().substring(0, 10),
        dueDate: taskToUpsert.dueDate,
        linkText: taskToUpsert.linkText,
        linkUrl: taskToUpsert.linkUrl,
        taskId,
      }
      analytics.trackActionCreator(goals.Goal_Task_Save_Clicked)(eventData)

      if (taskToUpsert.resource) {
        analytics.trackActionCreator(
          initiatives.Goal_Task_Academy_Resource_Added
        )(eventData)
      }

      if (taskToUpsert.linkText) {
        analytics.trackActionCreator(initiatives.Goal_Task_URL_Text_Added)(
          eventData
        )
      }

      if (taskToUpsert.linkUrl) {
        analytics.trackActionCreator(initiatives.Goal_Task_URL_Link_Added)(
          eventData
        )
      }

      if (taskToUpsert.owner) {
        const currentOwner = taskToUpsert?.owner?.id
        const assigneeUser = currentOwner ? teamMap[currentOwner] : null
        analytics.trackActionCreator(goals.Goal_Task_Assigned)({
          ...eventData,
          assigneeUserId: taskToUpsert.owner.id,
          assigneeUserEmail: assigneeUser?.email,
          assignerUserId: userId,
          goalTaskAssignationStatus: 'complete',
        })
      }

      if (taskToUpsert.dueDate) {
        analytics.trackActionCreator(initiatives.Goal_Task_DueDate_Submitted)(
          eventData
        )
      }
    }
    onClose()
  }

  const menuOptions = teamOptions.map((option) => (
    <MenuItem key={option.value} value={option.value}>
      {option.label}
    </MenuItem>
  ))

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      disableEnforceFocus
    >
      <TaskModalContainer>
        <TaskModalHeader>{taskAction}</TaskModalHeader>
        <TaskModalInputsContainer>
          <TextField
            id='taskModalInputName'
            label='Name this task'
            variant='outlined'
            size='small'
            value={domPurify.sanitize(taskTitle)}
            onChange={({ target: { value } }) => setTaskTitle(value)}
          />
          <FlexRow>
            <FormControl sx={{ width: '50%' }}>
              <InputLabel
                id='taskModalInputSelectLabel'
                sx={
                  shouldHighlightAssignOwner
                    ? { color: colors.illustration.skinTone2 }
                    : {}
                }
              >
                Assign to
              </InputLabel>
              <Select
                id='taskModalInputSelect'
                displayEmpty
                labelId='taskModalInputSelectLabel'
                label='Assign to'
                value={taskOwnerId}
                onChange={({ target: { value } }) => {
                  setTaskOwnerId(parseInt(String(value), 10))
                }}
                onClose={() => setIsAssignmentOpen(false)}
                onOpen={() => setIsAssignmentOpen(true)}
                open={isAssignmentOpen}
                sx={{
                  height: '40px',
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  '& .MuiOutlinedInput-notchedOutline, &:hover > .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: shouldHighlightAssignOwner
                        ? colors.illustration.skinTone2
                        : '',
                    },
                }}
              >
                <MenuItem value={0}>
                  {isAssignmentOpen ? 'None' : 'Select a team member'}
                </MenuItem>
                {menuOptions}
              </Select>
            </FormControl>
            <DatePicker
              date={dueDate ? dueDate.format('YYYY-MM-DD') : null}
              label='Due Date'
              onDateSelected={setDueDate}
              styles={{ width: '50%' }}
            />
          </FlexRow>
          <AcademyResourceSearch
            onSelectResource={setAcademyResource}
            resource={academyResource}
          />
          <TaskModalSubHeader>
            Add Link{' '}
            <TooltipSong
              icon={<InfoIcon style={{ color: colors.feedback.infoDark }} />}
              text='You can include an additional URL to your task to reference
                surveys or docs, this will appear as an additional line in your
                task'
              placement='right-end'
            />
          </TaskModalSubHeader>
          <TextField
            id='taskModalInputLinkText'
            label='Text'
            variant='outlined'
            size='small'
            value={domPurify.sanitize(taskLinkText)}
            onChange={({ target: { value } }) => setTaskLinkText(value)}
          />
          <TextField
            id='taskModalInputLinkUrl'
            label='URL'
            variant='outlined'
            size='small'
            value={domPurify.sanitize(taskLinkUrl)}
            onChange={({ target: { value } }) => setTaskLinkUrl(value)}
          />
        </TaskModalInputsContainer>
        <TaskModalButtonContainer>
          <Button
            label='Cancel'
            onClick={handleClose}
            type='button'
            variant='tertiary'
          />
          <Button
            label='Save'
            onClick={handleSubmitTask}
            type='button'
            variant='primary'
          />
        </TaskModalButtonContainer>
      </TaskModalContainer>
    </Modal>
  )
}
